import React, { useState, useEffect, useRef } from 'react';
import { Bell, Users, Check, Sparkles, ArrowRight, Loader, CalendarDays } from 'lucide-react';
import AppointmentInviteActions from './AppointmentInviteActions';
import notificationsService from '../../services/notificationsService';
import teamsService from '../../services/teamsService';
import { useAuthStore } from '../../store/authStore';
import { useNotificationStore } from '../../store/notificationStore';
import NotificationsModal from './NotificationsModal';
import { addNotificationListener, addUserRejoinListener } from '../../services/channelService';
import { useBadgeStore } from '../../store/badgeStore';
import OsScroll from './OsScroll';

const DROPDOWN_LIMIT = 5;

function formatTime(timestamp) {
  const s = Math.floor((Date.now() - new Date(timestamp)) / 1000);
  if (s < 60) return 'Just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 7 * 86400) return `${Math.floor(s / 86400)}d ago`;
  return new Date(timestamp).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
}

function NotifIcon({ type }) {
  let Icon = Bell;
  let bg = 'rgba(var(--accent-rgb), 0.85)';

  if (type === 'team_invitation') { Icon = Users; }
  else if (type && type.startsWith('appointment_')) { Icon = CalendarDays; }
  else if (type === 'workspace_shared' || type === 'collection_shared' || type === 'request_shared') {
    Icon = Check;
  } else if (type === 'workspace_updated') {
    Icon = Sparkles;
    bg = 'rgba(100,100,120,0.55)';
  }

  return (
    <div style={{
      width: 22, height: 22, borderRadius: 6, flexShrink: 0,
      background: bg,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <Icon size={11} style={{ color: 'var(--accent-fg, #fff)' }} />
    </div>
  );
}

function useDark() {
  const [isDark, setIsDark] = useState(() => document.documentElement.classList.contains('dark'));
  useEffect(() => {
    const obs = new MutationObserver(() => setIsDark(document.documentElement.classList.contains('dark')));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);
  return isDark;
}

const NotificationIcon = ({ showToast, onTeamJoined }) => {
  const isDark = useDark();
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const { isAuthenticated } = useAuthStore();

  // Initial fetch — runs once when authenticated, and again on every
  // user:<id> rejoin (COMMS-07): that channel carries `notification` pushes
  // with no gap-fill, so a reconnect after any blip silently drops whatever
  // fired while disconnected unless something refetches on rejoin.
  useEffect(() => {
    if (!isAuthenticated) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await notificationsService.getNotifications();
        if (!cancelled) {
          setNotifications(res.notifications);
          setUnreadCount(res.unread_count);
        }
      } catch {} finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    const removeRejoin = addUserRejoinListener(() => { load(); });
    return () => { cancelled = true; removeRejoin(); };
  }, [isAuthenticated]);

  // Real-time: prepend notification pushed via WebSocket
  useEffect(() => {
    if (!isAuthenticated) return;
    const remove = addNotificationListener((notif) => {
      setNotifications(prev => [notif, ...prev]);
      setUnreadCount(prev => prev + 1);
    });
    return remove;
  }, [isAuthenticated]);

  // Mirror the unread count out to the global badge store so the desktop
  // app-icon badge can add it to the chat unread total. Clears on logout.
  const setNotifUnread = useBadgeStore((s) => s.setNotifUnread);
  useEffect(() => {
    setNotifUnread(isAuthenticated ? unreadCount : 0);
  }, [unreadCount, isAuthenticated, setNotifUnread]);

  const handleToggle = (e) => {
    e.stopPropagation();
    const opening = !showDropdown;
    if (opening) {
      window.dispatchEvent(new CustomEvent('titlebar-dropdown-open', { detail: 'notifications' }));
    }
    setShowDropdown(opening);
  };

  useEffect(() => {
    if (!showDropdown) return;
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowDropdown(false);
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [showDropdown]);

  // Close when another titlebar dropdown opens
  useEffect(() => {
    const handler = (e) => { if (e.detail !== 'notifications') setShowDropdown(false); };
    window.addEventListener('titlebar-dropdown-open', handler);
    return () => window.removeEventListener('titlebar-dropdown-open', handler);
  }, []);

  const markAsRead = async (id, e) => {
    e.stopPropagation();
    try {
      await notificationsService.markAsRead(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {}
  };

  const markAllAsRead = async () => {
    try {
      await notificationsService.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch {
      showToast?.('Failed to mark all as read', 'error');
    }
  };

  const handleInvite = async (notification, action) => {
    const { success, error, info } = useNotificationStore.getState();
    try {
      const token = notification.data?.invitation_token;
      if (!token) return;
      if (action === 'accept') {
        const res = await teamsService.acceptInvitation(token);
        success(`Joined "${res.team.name}"!`);
        onTeamJoined?.(res.team);
      } else {
        await teamsService.declineInvitation(token);
        info('Invitation declined');
      }
      setNotifications(prev => prev.map(n =>
        n.id === notification.id
          ? { ...n, is_read: true, data: { ...n.data, invitation_status: action === 'accept' ? 'accepted' : 'declined' } }
          : n
      ));
      if (!notification.is_read) setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      error(err.message || 'Failed to respond to invitation');
    }
  };

  if (!isAuthenticated) return null;

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell button */}
      <button
        onClick={handleToggle}
        className="relative px-1 bg-transparent text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:text-zinc-400 dark:hover:text-zinc-100 dark:hover:bg-zinc-800 rounded-lg transition-colors cursor-pointer"
        style={{ WebkitAppRegion: 'no-drag' }}
        title="Notifications"
      >
        <Bell size={16} />
        {unreadCount > 0 && (
          <span
            className="absolute flex items-center justify-center font-semibold"
            style={{
              bottom: '-3px', right: '-1px',
              width: 13, height: 13,
              borderRadius: 9999,
              background: 'var(--accent)',
              color: 'var(--accent-fg, #fff)',
              fontSize: 8,
              lineHeight: 1,
            }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Full history modal */}
      <NotificationsModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        notifications={notifications}
        setNotifications={setNotifications}
        setUnreadCount={setUnreadCount}
        onInviteAction={onTeamJoined}
      />

      {/* Dropdown */}
      {showDropdown && (
        <div
          className="absolute z-50"
          style={{ right:0, top:'calc(100% + 8px)', width:300, borderRadius:12, overflow:'hidden', background: isDark ? '#161618' : '#fff', border: `1px solid ${isDark ? '#2e2e33' : '#e5e7eb'}`, boxShadow:'0 8px 32px rgba(0,0,0,0.18)' }}
        >
          {/* Header */}
          <div style={{ display:'flex', alignItems:'center', padding:'8px 14px', borderBottom:`1px solid ${isDark ? '#2e2e33' : '#e5e7eb'}`, marginTop:4 }}>
            <span style={{ fontSize:11, fontWeight:600, color:'var(--text)' }}>Notifications</span>
            {unreadCount > 0 && (
              <span style={{
                display:'flex', alignItems:'center', justifyContent:'center',
                background:'var(--accent)', color:'var(--accent-fg, #fff)', fontWeight:600,
                fontSize:10, minWidth:17, height:17, padding:'0 5px',
                borderRadius:99, marginLeft:7, marginRight:'auto',
              }}>
                {unreadCount}
              </span>
            )}
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                style={{ marginLeft:'auto', fontSize:11, color:'var(--text-muted)', background:'none', border:'none', cursor:'pointer', padding:0 }}
              >
                Mark all read
              </button>
            )}
          </div>

          {/* Body */}
          <OsScroll style={{ maxHeight: 400 }}>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader size={20} className="animate-spin text-gray-400 dark:text-zinc-500" />
              </div>
            ) : notifications.length === 0 ? (
              /* Empty state */
              <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'20px 16px', textAlign:'center', gap:5 }}>
                <div style={{ width:30, height:30, borderRadius:8, background:'rgba(var(--accent-rgb), 0.12)', display:'flex', alignItems:'center', justifyContent:'center', marginBottom:2 }}>
                  <Bell size={13} style={{ color:'var(--accent)', opacity:0.7 }} />
                </div>
                <p style={{ fontSize:11, fontWeight:600, color: isDark ? '#f4f4f5' : '#111827', margin:0 }}>You're all caught up</p>
                <p style={{ fontSize:10, color: isDark ? '#71717a' : '#9ca3af', margin:0 }}>New notifications will show up here.</p>
              </div>
            ) : (
              <div>
                {notifications.slice(0, DROPDOWN_LIMIT).map((n, i) => (
                  <div
                    key={n.id}
                    className={`px-3 py-2 transition-colors ${
                      !n.is_read
                        ? 'bg-violet-50/60 dark:bg-violet-950/20'
                        : 'hover:bg-gray-50 dark:hover:bg-zinc-800/50'
                    } ${i < notifications.length - 1 ? 'border-b border-gray-100 dark:border-zinc-800' : ''}`}
                    style={{
                      borderLeft: !n.is_read ? '3px solid var(--accent)' : '3px solid transparent',
                    }}
                  >
                    <div style={{display:'flex', flexDirection:'column', gap:4}}>
                      <div style={{display:'flex', alignItems:'center'}}>
                        <div style={{marginRight:8, flexShrink:0}}>
                          <NotifIcon type={n.type} />
                        </div>
                        <div style={{flex:1, minWidth:0, display:'flex', alignItems:'center', justifyContent:'space-between', gap:6}}>
                          <span style={{ fontSize:11, fontWeight:600, color: isDark ? '#fff' : '#111827', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                            {n.title}
                          </span>
                          <span style={{ fontSize:10, color: isDark ? '#71717a' : '#9ca3af', flexShrink:0 }}>
                            {formatTime(n.created_at)}
                          </span>
                        </div>
                      </div>
                      <div>
                        <p style={{ fontSize:11, color: isDark ? '#a1a1aa' : '#6b7280', margin:0, lineHeight:1.5 }}>
                          {n.message}
                        </p>
                        {/* Invite actions */}
                        {n.type === 'team_invitation' && (() => {
                          const invStatus = n.data?.invitation_status;
                          if (invStatus === 'accepted') {
                            return (
                              <div className="flex items-center gap-1.5 mt-2">
                                <Check size={12} style={{ color: '#16a34a' }} />
                                <span style={{ fontSize: 11, color: '#16a34a', fontWeight: 500 }}>Joined team</span>
                              </div>
                            );
                          }
                          if (invStatus === 'declined') {
                            return (
                              <div className="flex items-center gap-1.5 mt-2">
                                <span style={{ fontSize: 11, color: isDark ? '#71717a' : '#9ca3af', fontWeight: 500 }}>Invitation declined</span>
                              </div>
                            );
                          }
                          return (
                            <div className="flex items-center gap-2 mt-2">
                              <button
                                onClick={() => handleInvite(n, 'decline')}
                                style={{
                                  padding:'3px 12px', fontSize:12, fontWeight:500, borderRadius:6, cursor:'pointer',
                                  border: `1px solid ${isDark ? '#52525b' : '#e5e7eb'}`,
                                  background: isDark ? '#27272a' : '#f3f4f6',
                                  color: isDark ? '#a1a1aa' : '#4b5563',
                                }}
                              >
                                Decline
                              </button>
                              <button
                                onClick={() => handleInvite(n, 'accept')}
                                className="px-3 py-1 text-xs font-semibold rounded-md transition-colors"
                                style={{ background: 'var(--accent)', color: 'var(--accent-fg, #fff)' }}
                              >
                                Accept
                              </button>
                            </div>
                          );
                        })()}
                        {/* Appointment invite actions — one shared component
                            for dropdown + modal (PLAN §3.4), responding via
                            THE single calendarInvitesService path. */}
                        {n.type === 'appointment_invitation' && (
                          <AppointmentInviteActions
                            notification={n}
                            isDark={isDark}
                            onResponded={(notification, status) => {
                              setNotifications(prev => prev.map(x =>
                                x.id === notification.id
                                  ? { ...x, is_read: true, data: { ...x.data, invitation_status: status } }
                                  : x
                              ));
                              if (!notification.is_read) setUnreadCount(prev => Math.max(0, prev - 1));
                            }}
                          />
                        )}
                        {/* Mark as read for non-invite unread */}
                        {!n.is_read && n.type !== 'team_invitation' && n.type !== 'appointment_invitation' && (
                          <button
                            onClick={(e) => markAsRead(n.id, e)}
                            style={{ fontSize:10, color: 'var(--accent)', background: 'none', border: 'none', padding: 0, cursor: 'pointer', marginTop:3 }}
                          >
                            Mark as read
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </OsScroll>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="border-t border-gray-100 dark:border-zinc-800">
              <button
                onClick={() => { setShowDropdown(false); setShowModal(true); }}
                className="w-full flex items-center justify-center gap-1.5 px-4 py-3 text-xs font-medium text-gray-500 dark:text-zinc-400 hover:text-gray-800 dark:hover:text-zinc-200 transition-colors bg-transparent"
              >
                {notifications.length > DROPDOWN_LIMIT
                  ? `View all notifications (${notifications.length})`
                  : 'View all notifications'
                }
                <ArrowRight size={12} />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationIcon;
